import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Hospital, Mail, Phone, MapPin, Clock } from "lucide-react";

export default function ContactUs() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="rounded-lg bg-blue-600 p-2">
              <Hospital className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">AMCARE</h1>
          </Link>
          <Button variant="outline" asChild>
            <Link to="/login">Login</Link>
          </Button>
        </div>
      </header>
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Contact Us</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              We're here to help! Reach out to us for any questions or support.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-4">Get in Touch</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-hospital-primary mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Email Support</h3>
                    <p className="text-gray-700">
                      <a href="mailto:support@amcares.in" className="text-blue-600 hover:underline">
                        support@amcares.in
                      </a>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      For technical support and general inquiries
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-hospital-primary mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Phone Support</h3>
                    <p className="text-gray-700">
                      <a href="tel:+917799647888" className="text-blue-600 hover:underline">
                        +91 7799647888
                      </a>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Available during support hours
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-hospital-primary mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Support Hours</h3>
                    <p className="text-gray-700">
                      Monday - Friday: 9:00 AM - 6:00 PM IST
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      We respond to emails within 24 hours
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-hospital-primary mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Business Address</h3>
                    <p className="text-gray-700">
                      India
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      For detailed address, please contact via email
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="border-t pt-6">
              <h2 className="text-xl font-semibold mb-3">Support Categories</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Technical Support</h3>
                  <p className="text-sm text-gray-700">
                    For issues with login, features, or technical problems
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Billing & Payments</h3>
                  <p className="text-sm text-gray-700">
                    For subscription, payment, or refund inquiries
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Account Management</h3>
                  <p className="text-sm text-gray-700">
                    For account setup, user management, or access issues
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">General Inquiries</h3>
                  <p className="text-sm text-gray-700">
                    For questions about features, pricing, or partnerships
                  </p>
                </div>
              </div>
            </section>

            <section className="border-t pt-6">
              <h2 className="text-xl font-semibold mb-3">Response Time</h2>
              <p className="text-gray-700">
                We strive to respond to all inquiries within 24 hours during business days. 
                For urgent matters, please mention "URGENT" in your email subject line.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="container mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p>&copy; {new Date().getFullYear()} AMCare. All rights reserved.</p>
          <div className="mt-2 flex justify-center gap-4">
            <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:underline">Terms & Conditions</Link>
            <Link to="/refund-policy" className="hover:underline">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

